/**
 * ContactPageHeader
 *
 * Mobile: busca → criar + menu de ações.
 * Desktop inline: busca → criar + menu, alinhados à direita dos chips.
 */

import React from 'react';
import SearchInput from '../../../components/ui/SearchInput';
import { AppIcon } from '../../../components/icons/AppIcon';
import ActionMenu, { ActionMenuItem } from '../../../components/shared/ActionMenu';
import { Button } from '../../../components/ui/Button';

interface ContactPageHeaderProps {
    onAddContact: () => void;
    onExportCsv?: () => void;
    onExportXlsx?: () => void;
    onImport?: () => void;
    isExporting?: boolean;
    searchValue: string;
    onSearchChange: (value: string) => void;
    variant?: 'mobile' | 'desktop-inline';
}

const ContactPageHeader: React.FC<ContactPageHeaderProps> = ({
    onAddContact,
    onExportCsv,
    onExportXlsx,
    onImport,
    isExporting = false,
    searchValue,
    onSearchChange,
    variant = 'mobile',
}) => {
    const menuActions: ActionMenuItem[] = [
        ...(onImport
            ? [
                  {
                      label: 'Importar',
                      icon: <AppIcon name="upload" className="size-4" aria-hidden />,
                      onClick: onImport,
                  },
              ]
            : []),
        ...(onExportCsv
            ? [
                  {
                      label: isExporting ? 'Exportando...' : 'Exportar CSV',
                      icon: <AppIcon name="download" className="size-4" aria-hidden />,
                      onClick: onExportCsv,
                      disabled: isExporting,
                  },
              ]
            : []),
        ...(onExportXlsx
            ? [
                  {
                      label: isExporting ? 'Exportando...' : 'Exportar Excel',
                      icon: <AppIcon name="download" className="size-4" aria-hidden />,
                      onClick: onExportXlsx,
                      disabled: isExporting,
                  },
              ]
            : []),
    ];

    const showActionMenu = menuActions.length > 0;

    if (variant === 'desktop-inline') {
        return (
            <div
                role="toolbar"
                aria-label="Busca e ações de contatos"
                className="hidden shrink-0 md:ml-auto md:flex md:items-center md:gap-s2"
            >
                <div className="w-64 shrink-0">
                    <SearchInput
                        value={searchValue}
                        onChange={onSearchChange}
                        placeholder="Pesquisar contatos..."
                    />
                </div>
                <Button type="button" onClick={onAddContact} className="shrink-0 whitespace-nowrap">
                    Novo Contato
                </Button>
                {showActionMenu ? <ActionMenu actions={menuActions} /> : null}
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col gap-s3 md:hidden">
            <SearchInput
                value={searchValue}
                onChange={onSearchChange}
                placeholder="Buscar contatos..."
                className="min-h-11 w-full"
            />
            <div
                role="toolbar"
                aria-label="Ações de contatos"
                className="flex min-h-11 items-stretch gap-s2"
            >
                <Button
                    type="button"
                    onClick={onAddContact}
                    className="min-h-11 flex-1 touch-manipulation"
                >
                    Novo Contato
                </Button>
                {showActionMenu ? (
                    <ActionMenu actions={menuActions} />
                ) : null}
            </div>
        </div>
    );
};

export default ContactPageHeader;
